import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  MessageSquarePlus,
  Search,
  Home,
  Plus,
  PanelLeftClose,
  Handshake,
  LogIn,
  User,
  LogOut,
  HelpCircle,
  Info,
  ChevronRight,
  MoreHorizontal,
  Trash2,
  FolderInput,
  MessageSquare,
  Clock,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import type { ConstructionSite, ChatProject } from "@/hooks/useProjects";

interface AppSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose?: () => void;
  onHowItWorks?: () => void;
  onAboutUs?: () => void;
  isLoggedIn?: boolean;
  sites?: ConstructionSite[];
  chats?: ChatProject[];
  activeChatId?: string | null;
  onSelectChat?: (id: string) => void;
  onNewChat?: () => void;
  onDeleteChat?: (id: string) => void;
  onAssignChat?: (chatId: string, siteId: string | null) => void;
  onAddSite?: (name: string) => void;
  guestInquiryIds?: string[];
  /** Number of messages in the current guest chat session (0 = no active chat) */
  guestChatMessageCount?: number;
}

export function AppSidebar({
  isOpen,
  onToggle,
  onClose,
  onHowItWorks,
  onAboutUs,
  isLoggedIn = false,
  sites = [],
  chats = [],
  activeChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  onAssignChat,
  onAddSite,
  guestInquiryIds,
  guestChatMessageCount = 0,
}: AppSidebarProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const newChatPath = "/";
  const displayChats = chats;
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [menuOpenChatId, setMenuOpenChatId] = useState<string | null>(null);
  const [showHistorySection, setShowHistorySection] = useState(false);
  const [showInquiriesSection, setShowInquiriesSection] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [showNewProjectInput, setShowNewProjectInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSupplier, setIsSupplier] = useState(false);
  const [inquiries, setInquiries] = useState<any[]>([]);

  useEffect(() => {
    if (!isLoggedIn) { setIsSupplier(false); return; }
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      supabase.from("suppliers").select("id").eq("user_id", session.user.id).maybeSingle().then(({ data }) => {
        setIsSupplier(!!data);
      });
    });
  }, [isLoggedIn]);

  // Load user inquiries + realtime
  const loadInquiries = useCallback(async () => {
    if (isLoggedIn) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data } = await supabase
        .from("inquiries")
        .select("id, title, status, created_at, supplier_quotes(id, total_price, supplier_id, customer_approved)")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: false });
      if (data) setInquiries(data);
    } else if (guestInquiryIds && guestInquiryIds.length > 0) {
      const { data } = await supabase
        .from("inquiries")
        .select("id, title, status, created_at, supplier_quotes(id, total_price, supplier_id, customer_approved)")
        .in("id", guestInquiryIds)
        .order("created_at", { ascending: false });
      if (data) setInquiries(data);
    } else {
      setInquiries([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, guestInquiryIds?.join(",")]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  // Realtime subscription for inquiries & quotes
  useEffect(() => {
    const channel = supabase
      .channel('sidebar-inquiries')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inquiries' }, () => {
        loadInquiries();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'supplier_quotes' }, () => {
        loadInquiries();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [loadInquiries]);

  const handleNewChat = () => {
    navigate(newChatPath);
    if (window.innerWidth < 1024) {
      if (onClose) onClose();
      else onToggle();
    }
    onNewChat?.();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const toggleSite = (siteId: string) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const unassignedChats = chats.filter((c) => !c.construction_site_id);
  const chatsBySite = (siteId: string) => chats.filter((c) => c.construction_site_id === siteId);

  const renderChatItem = (chat: ChatProject) => (
    <div key={chat.id} className="group flex items-center relative">
      <button
        onClick={() => {
          onSelectChat?.(chat.id);
          if (window.innerWidth < 1024) onToggle();
        }}
        className={`flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-left truncate transition-colors ${
          activeChatId === chat.id
            ? "bg-sidebar-accent text-sidebar-foreground font-medium"
            : "text-sidebar-foreground hover:bg-sidebar-accent/50"
        }`}
      >
        <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
        <span className="truncate">{chat.name}</span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpenChatId(menuOpenChatId === chat.id ? null : chat.id);
        }}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-sidebar-accent text-muted-foreground flex-shrink-0"
      >
        <MoreHorizontal className="w-3.5 h-3.5" />
      </button>

      {menuOpenChatId === chat.id && (
        <div className="absolute right-0 top-full z-50 mt-1 bg-popover border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
          {sites.length > 0 && (
            <>
              <p className="px-3 py-1 text-xs text-muted-foreground font-medium">{t("sidebar.assignTo")}</p>
              {sites.map((site) => (
                <button
                  key={site.id}
                  onClick={() => {
                    onAssignChat?.(chat.id, site.id);
                    setMenuOpenChatId(null);
                  }}
                  className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-accent text-left ${
                    chat.construction_site_id === site.id ? "text-primary font-medium" : "text-foreground"
                  }`}
                >
                  <Home className="w-3.5 h-3.5" />
                  <span className="truncate">{site.name}</span>
                </button>
              ))}
              {chat.construction_site_id && (
                <button
                  onClick={() => {
                    onAssignChat?.(chat.id, null);
                    setMenuOpenChatId(null);
                  }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-accent text-muted-foreground text-left"
                >
                  <FolderInput className="w-3.5 h-3.5" />
                  <span>{t("sidebar.unassign")}</span>
                </button>
              )}
              <div className="my-1 border-t border-border" />
            </>
          )}
          <button
            onClick={() => {
              onDeleteChat?.(chat.id);
              setMenuOpenChatId(null);
            }}
            className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-destructive/10 text-destructive text-left"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>{t("sidebar.deleteChat")}</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 z-30 lg:hidden"
          onClick={() => { onToggle(); setMenuOpenChatId(null); }}
        />
      )}

      <aside
        className={`fixed top-0 left-0 bottom-0 z-40 bg-sidebar flex flex-col transition-transform duration-200 ease-in-out w-[260px] border-r border-sidebar-border
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        onClick={() => setMenuOpenChatId(null)}
      >
        {/* How it works + Close */}
        <div className="px-3 h-12 flex items-center gap-1">
          <button
            onClick={() => onHowItWorks?.()}
            className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors"
          >
            <HelpCircle className="w-4 h-4 flex-shrink-0" />
            <span>{t("sidebar.howToUse")}?</span>
          </button>
          <button
            onClick={onToggle}
            className="p-2 rounded-xl text-muted-foreground hover:bg-sidebar-accent transition-colors"
          >
            <PanelLeftClose className="w-5 h-5" />
          </button>
        </div>

        {/* Top menu */}
        <nav className="flex-1 overflow-y-auto px-3 pt-3" onClick={(e) => e.stopPropagation()}>
          <div className="space-y-1">
            {/* 1. New Chat */}
            <button
              onClick={handleNewChat}
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
            >
              <MessageSquarePlus className="w-5 h-5 flex-shrink-0" />
              <span>{t("sidebar.newChat")}</span>
            </button>

            {/* 2. History (expandable) */}
            <button
              onClick={() => {
                if (!isLoggedIn) { setShowHistorySection(!showHistorySection); return; }
                setShowHistorySection(!showHistorySection);
              }}
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
            >
              <Clock className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{t("sidebar.history")}</span>
              {isLoggedIn && displayChats.length > 0 && (
                <span className="text-xs text-muted-foreground">{displayChats.length}</span>
              )}
              {!isLoggedIn && guestChatMessageCount > 0 && (
                <span className="text-xs text-muted-foreground">1</span>
              )}
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showHistorySection ? "rotate-90" : ""}`} />
            </button>
          </div>

          {/* Expanded History */}
          {showHistorySection && !isLoggedIn && (
            <div className="mt-1 space-y-0.5">
              {guestChatMessageCount > 0 && (
                <Link
                  to="/"
                  onClick={() => { if (window.innerWidth < 1024) onToggle(); }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-sidebar-foreground bg-sidebar-accent/50 w-full text-left"
                >
                  <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
                  <span className="truncate flex-1">{t("sidebar.currentChat")}</span>
                  <span className="text-xs text-muted-foreground">{guestChatMessageCount} {t("sidebar.messageCount")}</span>
                </Link>
              )}
              <div className="px-3 py-2">
                <p className="text-xs text-muted-foreground">
                  {t("sidebar.loginForHistory").split(t("sidebar.loginLink")).map((part, i, arr) =>
                    i < arr.length - 1 ? (
                      <span key={i}>{part}<Link to="/login" className="underline hover:text-foreground transition-colors">{t("sidebar.loginLink")}</Link></span>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  )}
                </p>
              </div>
            </div>
          )}
          {isLoggedIn && showHistorySection && (
            <div className="mt-1 space-y-0.5">
              {displayChats.length > 3 && (
                <div className="px-1 mb-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-transparent">
                    <Search className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 bg-transparent text-sm text-sidebar-foreground outline-none placeholder:text-muted-foreground"
                      placeholder={t("sidebar.searchChats")}
                    />
                  </div>
                </div>
              )}
              {displayChats
                .filter((c) => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map(renderChatItem)}
              {displayChats.length === 0 && (
                <p className="px-3 py-2 text-xs text-muted-foreground">{t("sidebar.noChatsYet")}</p>
              )}
              {searchQuery && displayChats.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                <p className="px-3 py-2 text-xs text-muted-foreground">{t("sidebar.noSearchResults")}</p>
              )}
            </div>
          )}

          <div className="my-3 border-t border-sidebar-border" />

          {/* 3. Inquiries & Quotes */}
          <div className="space-y-1">
            <button
              onClick={() => setShowInquiriesSection(!showInquiriesSection)}
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
            >
              <FileText className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{t("sidebar.inquiriesAndQuotes")}</span>
              {inquiries.length > 0 && (
                <span className="text-xs text-muted-foreground">{inquiries.length}</span>
              )}
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showInquiriesSection ? "rotate-90" : ""}`} />
            </button>
          </div>

          {/* Expanded Inquiries */}
          {showInquiriesSection && (
            <div className="mt-1 space-y-0.5">
              {!isLoggedIn && (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  {t("sidebar.loginForInquiries").split(t("sidebar.loginLink")).map((part, i, arr) =>
                    i < arr.length - 1 ? (
                      <span key={i}>{part}<Link to="/login" className="underline hover:text-foreground transition-colors">{t("sidebar.loginLink")}</Link></span>
                    ) : (
                      <span key={i}>{part}</span>
                    )
                  )}
                </p>
              )}
              {isLoggedIn && inquiries.length === 0 && (
                <p className="px-3 py-2 text-xs text-muted-foreground">{t("sidebar.noInquiriesYet")}</p>
              )}
              {inquiries.map((inq) => {
                const quotesCount = inq.supplier_quotes?.length || 0;
                return (
                  <div key={inq.id} className="space-y-0.5">
                    <Link
                      to={`/dashboard?inquiry=${inq.id}`}
                      onClick={() => { if (window.innerWidth < 1024) onToggle(); }}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors w-full text-left"
                    >
                      <FileText className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="truncate flex-1">{inq.title}</span>
                      {quotesCount > 0 && (
                        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                          {quotesCount} {quotesCount === 1 ? t("sidebar.quote") : t("sidebar.quotes")}
                        </span>
                      )}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </nav>

        {/* Bottom actions */}
        <div className="px-3 pb-2 pt-2 space-y-1 border-t border-sidebar-border">
          {isLoggedIn ? (
            <>
              {isSupplier && (
                <Link
                  to="/supplier/dashboard"
                  className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <Handshake className="w-5 h-5 flex-shrink-0" />
                  <span>{t("sidebar.supplierDashboard")}</span>
                </Link>
              )}
              <Link
                to="/profile"
                className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors"
              >
                <User className="w-5 h-5 flex-shrink-0" />
                <span>{t("sidebar.settings")}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors w-full text-left"
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <span>{t("sidebar.logout")}</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors"
              >
                <LogIn className="w-5 h-5 flex-shrink-0" />
                <span>{t("sidebar.login")}</span>
              </Link>
              <Link
                to="/login"
                className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors"
              >
                <User className="w-5 h-5 flex-shrink-0" />
                <span>{t("topbar.register")}</span>
              </Link>
            </>
          )}
        </div>

        {/* Supplier CTA */}
        {!(isLoggedIn && isSupplier) && (
          <div className="px-3 pb-2 pt-1 space-y-1 border-t border-sidebar-border">
            <p className="px-3 py-2 text-xs text-muted-foreground leading-relaxed">
              {t("sidebar.supplierCta")}
            </p>
            <Link
              to="/supplier/register"
              className="flex items-center gap-3.5 px-3 py-2.5 rounded-xl text-sidebar-foreground text-sm hover:bg-sidebar-accent transition-colors"
            >
              <Handshake className="w-5 h-5 flex-shrink-0" />
              <span>{t("sidebar.becomeSupplier")}</span>
            </Link>
          </div>
        )}

        {/* Language + About Us - very bottom */}
        <div className="px-3 pb-3 pt-2 flex items-center justify-between border-t border-sidebar-border">
          <div className="flex items-center gap-1">
            <LanguageSwitcher dropUp />
          </div>
          <Link
            to="/about"
            onClick={() => {
              if (window.innerWidth < 1024) {
                if (onClose) onClose();
                else onToggle();
              }
            }}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:bg-muted/70 hover:text-foreground transition-colors"
          >
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{t("sidebar.aboutUs")}</span>
          </Link>
        </div>
      </aside>
    </>
  );
}
